/**
 * Boot-time URL attribution + CookieYes consent.
 * Attribution is held in memory for the first Pulse event only (no long TTL).
 */

export type PendingAttribution = {
  click_id: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  /** Campaign URL `page_id` query param, if present. */
  page_id: string | null;
  /** Campaign URL `http_referrer` / `referrer` query param — not document.referrer. */
  http_referrer: string | null;
};

let pendingAttribution: PendingAttribution | null = null;
let attributionCaptured = false;

const pick = (params: URLSearchParams, key: string): string | null => {
  const v = params.get(key);
  return v && v.trim() ? v.trim() : null;
};

/** Read utm + click ids + campaign page_id from the current URL once (call at App boot). */
export const captureAttributionFromUrl = (
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): PendingAttribution | null => {
  if (attributionCaptured) {
    return pendingAttribution;
  }
  attributionCaptured = true;

  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const fbclid = pick(params, 'fbclid');
    const ttclid = pick(params, 'ttclid');
    const ScCid = pick(params, 'ScCid') || pick(params, 'sccid');
    const click_id = fbclid || ttclid || ScCid || null;
    const utm_source = pick(params, 'utm_source');
    const utm_medium = pick(params, 'utm_medium');
    const utm_campaign = pick(params, 'utm_campaign');
    const page_id = pick(params, 'page_id');
    const http_referrer =
      pick(params, 'http_referrer') || pick(params, 'referrer');

    if (
      !click_id &&
      !utm_source &&
      !utm_medium &&
      !utm_campaign &&
      !page_id &&
      !http_referrer
    ) {
      pendingAttribution = null;
      return null;
    }

    pendingAttribution = {
      click_id,
      utm_source,
      utm_medium,
      utm_campaign,
      page_id,
      http_referrer,
    };
    return pendingAttribution;
  } catch {
    pendingAttribution = null;
    return null;
  }
};

export const peekPendingAttribution = (): PendingAttribution | null =>
  pendingAttribution;

/** Consume pending attribution (first event only). */
export const consumePendingAttribution = (): PendingAttribution | null => {
  const current = pendingAttribution;
  pendingAttribution = null;
  return current;
};

/** Test helper — reset module state. */
export const __resetAttributionForTests = (): void => {
  pendingAttribution = null;
  attributionCaptured = false;
};

export type ConsentFlags = {
  cookie_consent_advertisement: boolean | null;
  cookie_consent_functional: boolean | null;
  cookie_consent_necessary: boolean | null;
};

/** CookieYes `cookieyes-consent` value: `consentid:…,necessary:yes,advertisement:no,…` */
export const parseCookieYesConsentValue = (
  cookieValue: string
): Record<string, boolean | null> => {
  const categories: Record<string, boolean | null> = {};
  for (const part of cookieValue.split(',')) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) continue;
    const key = part.slice(0, colonIndex).trim().toLowerCase();
    const raw = part.slice(colonIndex + 1).trim().toLowerCase();
    if (raw === 'yes') categories[key] = true;
    else if (raw === 'no') categories[key] = false;
  }
  return categories;
};

const toConsentFlag = (
  categories: Record<string, boolean | null>,
  category: string
): boolean | null => categories[category] ?? null;

export const readCookieYesConsent = (
  cookieString: string = typeof document !== 'undefined' ? document.cookie : ''
): ConsentFlags => {
  const empty: ConsentFlags = {
    cookie_consent_advertisement: null,
    cookie_consent_functional: null,
    cookie_consent_necessary: null,
  };
  try {
    const match = cookieString.match(/(?:^|;\s*)cookieyes-consent=([^;]*)/);
    if (!match) return empty;
    const value = decodeURIComponent(match[1]);
    const categories = parseCookieYesConsentValue(value);
    return {
      cookie_consent_advertisement: toConsentFlag(categories, 'advertisement'),
      cookie_consent_functional: toConsentFlag(categories, 'functional'),
      cookie_consent_necessary: toConsentFlag(categories, 'necessary'),
    };
  } catch {
    return empty;
  }
};

export const readCroVariants = (): string | null => {
  try {
    const wf = (window as unknown as { WF?: unknown }).WF;
    if (wf && typeof wf === 'object') {
      return JSON.stringify(wf);
    }
  } catch (_) {}
  return null;
};
